'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import type { ComponentProps, ComponentType } from 'react';

import type { XlsxViewerPreview as XlsxViewerPreviewComponent } from './extend-ui/ui/xlsx-viewer';
import { PreviewStatus } from './preview-state';

// Next 16.3.1 standalone tracing follows the XLSX viewer into its Wasm dependency
// and panics (SERVICING-13092, vercel/next.js#96897). This check is constant-folded
// per environment, so the server build removes the dynamic import from its trace.
// Remove it after vercel/next.js#97353 is released. Keep the loading gate below,
// because the server fallback intentionally renders nothing.
const XlsxViewerPreview: ComponentType<ComponentProps<typeof XlsxViewerPreviewComponent>> =
  typeof window === 'undefined'
    ? () => null
    : dynamic(() => import('./extend-ui/ui/xlsx-viewer').then((module) => module.XlsxViewerPreview), {
        loading: () => <PreviewStatus>Loading spreadsheet preview...</PreviewStatus>,
        ssr: false,
      });

function useHasMounted() {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  return hasMounted;
}

export function XlsxArtifactPreview({ sourceUrl, title }: { sourceUrl: string; title: string }) {
  const hasMounted = useHasMounted();

  if (!hasMounted) {
    return <PreviewStatus>Loading spreadsheet preview...</PreviewStatus>;
  }

  return (
    <XlsxViewerPreview
      className="h-full min-h-[480px]"
      fileName={title}
      isDark={false}
      onIsDarkChange={() => undefined}
      showDownload={false}
      showToolbar={false}
      showUpload={false}
      src={sourceUrl}
    />
  );
}
